import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuditLogPageDto, AuditLogRowDto } from '@gc/shared';

import { api } from '@/lib/api';
import { auditLogPath, type AuditFilters } from './auditLabels';

/**
 * One person the log can be filtered to — `GET /audit-log/actors`. Mirrors
 * `AuditLogActorDto` in the API, which lives there only because `@gc/shared`
 * was frozen when the route was added.
 */
export interface AuditActor {
  id: string;
  name: string;
  isActive: boolean;
}

/**
 * The audit log, one cursor page at a time.
 *
 * A filter change starts again from the newest row; "Load more" appends the
 * next page. A GENERATION counter guards both: an answer that arrives after
 * the filters moved on belongs to a question nobody is asking any more, and
 * appending it would splice two different searches into one list.
 */
export function useAuditLog(filters: AuditFilters, enabled: boolean) {
  const [rows, setRows] = useState<AuditLogRowDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const generation = useRef(0);
  const path = auditLogPath(filters);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    api
      .get<AuditLogPageDto>(path, controller.signal)
      .then((page) => {
        if (mine !== generation.current) return;
        setRows(page.rows);
        setNextCursor(page.nextCursor);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted || mine !== generation.current) return;
        setRows([]);
        setNextCursor(null);
        setError(caught);
      })
      .finally(() => {
        if (mine === generation.current) setLoading(false);
      });
    return () => controller.abort();
  }, [path, enabled]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    const mine = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.get<AuditLogPageDto>(auditLogPath(filters, nextCursor));
      if (mine !== generation.current) return;
      setRows((current) => [...current, ...page.rows]);
      setNextCursor(page.nextCursor);
    } catch (caught) {
      if (mine === generation.current) setError(caught);
    } finally {
      if (mine === generation.current) setLoadingMore(false);
    }
  }, [filters, nextCursor]);

  return { rows, nextCursor, loading, loadingMore, error, loadMore };
}

/** Everyone the Person filter can name. A failure leaves the filter empty, not the page. */
export function useAuditActors(enabled: boolean): AuditActor[] {
  const [actors, setActors] = useState<AuditActor[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .get<AuditActor[]>('/audit-log/actors')
      .then((list) => {
        if (!cancelled) setActors(list);
      })
      .catch(() => {
        // The list still works; only the Person filter is left empty.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return actors;
}
